import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Form,
  FormField,
  Grid,
  Header,
  Input,
  Select,
  SpaceBetween,
  Toggle,
  RadioGroup,
  Multiselect,
  SelectProps,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import routePaths from 'src/contstant/routePaths';
import { ScheduleStatus } from 'src/contstant/commonLabels';
import { useFormValidation } from 'src/hooks/useFormValidation';
import useUserProfile from 'src/hooks/useUserProfile';
import {
  AuditScheduleValidator,
  ScheduleValidationPaths,
} from 'src/validations/AuditScheduleValidator';
import {
  AuditSchedule,
  CreateAuditScheduleRequest,
  EndConditionType,
  RecurrenceType,
  ScheduleDetails,
  UpdateAuditScheduleRequest,
} from 'src/types/helios/AuditSchedule';
import ScheduleComponent from './ScheduleDates';
import { useSchedules } from 'src/store/schedules/useSchedules';
import RequiredFieldLabel from '../RequiredFieldLabel';
import { useTemplates } from 'src/store/templates/useTemplates';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';
import {
  checkFeaturePermission,
  convertList,
  formatDate,
  getAuditTypeOptions,
} from 'src/utils/generalUtils';
import {
  AUDIT_TYPE_LABELS,
  AuditTypeOptions,
  ScheduleType,
} from 'src/contstant/commonLabels';
import ApiStatusFlashBar from '../ApiStatusFlashBar';
import { get, isEmpty, isEqual } from 'lodash';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import WarningModal from '../WarningModal';

// ScheduleDetails is now imported from ScheduleComponent

export interface AuditScheduleParams {
  name: string;
  description: string;
  isActive: boolean;
  program: string;
  businessUnitId: string;
  auditType: string;
  templateId: string[];
  scheduleType: string;
  scheduleDetails: ScheduleDetails;
}

interface AuditScheduleFormProps {
  auditSchedule?: AuditSchedule | null;
  templateId?: string;
  copyFromSchedule?: Partial<AuditSchedule>;
  onCancel: () => void;
}

const AuditScheduleForm = ({
  onCancel,
  templateId,
  auditSchedule,
  copyFromSchedule,
}: AuditScheduleFormProps) => {
  // Check if schedule is completed and should be read-only
  const isCompleted = auditSchedule?.status === ScheduleStatus.COMPLETED;
  const navigate = useNavigate();

  const {
    getUserId,
    getPersona,
    getUserBusinessUnitId,
    getUserProgramId,
    currentUser,
    getDivisions,
  } = useUserProfile();
  const {
    createSchedule,
    updateSchedule,
    createScheduleApiStatus,
    updateScheduleApiStatus,
    resetApiStatus,
  } = useSchedules();
  const { checkUserPermissions, userPermission } = useUsers();
  const { templatesList, loadTemplatesList } = useTemplates();
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<boolean | null>(null);
  const initValues: AuditScheduleParams = {
    name: copyFromSchedule?.name || auditSchedule?.name || '',
    description: copyFromSchedule?.description || auditSchedule?.description || '',
    isActive: copyFromSchedule?.isActive ?? auditSchedule?.isActive ?? true,
    businessUnitId:
      copyFromSchedule?.businessUnit ||
      auditSchedule?.businessUnit ||
      getUserBusinessUnitId(),
    program: copyFromSchedule?.program || auditSchedule?.program || getUserProgramId(),
    auditType: copyFromSchedule?.auditType || auditSchedule?.auditType || '',
    scheduleType: copyFromSchedule?.scheduleType || auditSchedule?.scheduleType || 'ONCE',
    templateId:
      copyFromSchedule?.auditTemplateIds || auditSchedule?.auditTemplateIds || [],
    scheduleDetails: (copyFromSchedule?.scheduleDetails ||
      auditSchedule?.scheduleDetails) as ScheduleDetails,
  };

  const [scheduleParams, setScheduleParams] = useState<AuditScheduleParams>(initValues);
  const { fieldValidationError, validate } = useFormValidation(
    scheduleParams,
    AuditScheduleValidator(),
  );

  const hasChanges = useCallback(() => {
    if (!auditSchedule) return true; // For new schedules, always allow save
    return (
      scheduleParams.name !== initValues.name ||
      scheduleParams.description !== initValues.description ||
      scheduleParams.isActive !== initValues.isActive ||
      scheduleParams.auditType !== initValues.auditType ||
      scheduleParams.scheduleType !== initValues.scheduleType ||
      !isEqual(scheduleParams.templateId, initValues.templateId) ||
      !isEqual(scheduleParams.scheduleDetails, initValues.scheduleDetails)
    );
  }, [scheduleParams, initValues, auditSchedule]);

  // Constants for schedule details until TypeScript client integration
  const DEFAULT_DAILY_RECURRENCE = { type: 'DAILY', interval: 1 };
  const DEFAULT_END_CONDITION = { type: 'NEVER' };

  const handleCopyToNew = useCallback(() => {
    if (!auditSchedule) return;

    // Navigate to create form with copied data
    navigate(routePaths.SCHEDULE_DETAILS.path, {
      state: {
        schedule: undefined, // No existing schedule (create mode)
        copyFromSchedule: {
          name: `Copy of ${auditSchedule.name}`,
          description: auditSchedule.description,
          auditType: auditSchedule.auditType,
          scheduleType: auditSchedule.scheduleType,
          auditTemplateIds: auditSchedule.auditTemplateIds,
          businessUnit: auditSchedule.businessUnit,
          program: auditSchedule.program,
          // Reset schedule-specific fields
          isActive: true,
          scheduleDetails:
            auditSchedule.scheduleType === 'ONCE'
              ? { oneTime: { date: '' } }
              : {
                  recurring: {
                    startDate: '',
                    recurrencePattern:
                      auditSchedule.scheduleDetails.recurring?.recurrencePattern ||
                      DEFAULT_DAILY_RECURRENCE,
                    endCondition: DEFAULT_END_CONDITION,
                  },
                },
        },
      },
    });
  }, [auditSchedule, navigate]);

  const isAllowToCaliberationAudit = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.CALIBRATION_AUDIT.EDIT_CALIBRATION_AUDIT,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  const isAllowToProgressAudit = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.PROGRESS_AUDIT.EDIT_PROGRESS_AUDIT,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  const isAllowToSituationAudit = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.SITUATION_AUDIT.EDIT_SITUATION_AUDIT,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  const isAllowToMpaAudit = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.MPA_AUDIT.EDIT_MPA_AUDIT,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  const isAllowToTestAudit = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.TEST_AUDIT.EDIT_TEST_AUDIT,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  const isEditSchedule = useCallback(() => {
    return checkFeaturePermission(
      getPersona(),
      permissionAccessConfig.SCHEDULE.EDIT_SCHEDULE,
      userPermission?.permissions,
    );
  }, [userPermission, getPersona()]);

  // Handle API status changes
  useEffect(() => {
    if (createScheduleApiStatus.isSuccess || updateScheduleApiStatus.isSuccess) {
      onCancel(); // Close the form after successful API call
    }
  }, [createScheduleApiStatus.isSuccess, updateScheduleApiStatus.isSuccess, onCancel]);

  useEffect(() => {
    if (templateId) {
      setScheduleParams((prevState) => ({
        ...prevState,
        templateId: [templateId],
      }));
    }
    loadTemplatesList({
      program: getUserProgramId(),
      businessUnitId: getUserBusinessUnitId(),
    });

    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      permissions: [
        permissionAccessConfig.CALIBRATION_AUDIT.EDIT_CALIBRATION_AUDIT,
        permissionAccessConfig.PROGRESS_AUDIT.EDIT_PROGRESS_AUDIT,
        permissionAccessConfig.SITUATION_AUDIT.EDIT_SITUATION_AUDIT,
        permissionAccessConfig.MPA_AUDIT.EDIT_MPA_AUDIT,
        permissionAccessConfig.TEST_AUDIT.EDIT_TEST_AUDIT,
        permissionAccessConfig.SCHEDULE.EDIT_SCHEDULE,
      ],
      divisions: getDivisions(),
    });

    return () => {
      resetApiStatus();
    };
  }, [currentUser]);

  // Update schedule details when schedule type changes
  useEffect(() => {
    if (scheduleParams.scheduleType === ScheduleType.ONCE) {
      setScheduleParams((prev) => ({
        ...prev,
        scheduleDetails: {
          oneTime: {
            date: prev?.scheduleDetails?.oneTime?.date,
          },
        },
      }));
    } else if (
      scheduleParams.scheduleType === ScheduleType.RECURRING &&
      !scheduleParams.scheduleDetails.recurring
    ) {
      setScheduleParams((prev) => ({
        ...prev,
        scheduleDetails: {
          recurring: {
            startDate: undefined,
            recurrencePattern: {
              type: RecurrenceType.DAILY,
              interval: 1,
            },
            endCondition: {
              type: EndConditionType.NEVER,
            },
          },
        },
      }));
    }
  }, [scheduleParams.scheduleType]);

  const renderActionButton = () => {
    const onCancelClick = () => {
      onCancel();
    };

    const checkDatFormat = (scheduleDetails: ScheduleDetails): ScheduleDetails => {
      if (scheduleDetails?.oneTime?.date) {
        scheduleDetails.oneTime.date = formatDate(scheduleDetails.oneTime.date);
      }
      if (scheduleDetails?.recurring?.startDate) {
        scheduleDetails.recurring.startDate = formatDate(
          scheduleDetails.recurring.startDate,
        );
      }
      if (scheduleDetails?.recurring?.endCondition?.endDate) {
        scheduleDetails.recurring.endCondition.endDate = formatDate(
          scheduleDetails.recurring.endCondition.endDate,
        );
      }
      return scheduleDetails;
    };

    const onSaveClick = () => {
      if (validate()) {
        const userId = getUserId();

        // Transform schedule details to API format

        if (auditSchedule) {
          // Update existing schedule
          const updateRequest: UpdateAuditScheduleRequest = {
            scheduleId: auditSchedule.scheduleId,
            name: scheduleParams.name,
            program: scheduleParams.program,
            businessUnitId: scheduleParams.businessUnitId,
            description: scheduleParams.description,
            auditType: scheduleParams.auditType,
            scheduleType: scheduleParams.scheduleType,
            scheduleDetails: checkDatFormat(scheduleParams.scheduleDetails),
            auditExpirationSettings: {
              expirationEnabled: true,
              expirationDays: 30,
            },
            auditTemplateIds: scheduleParams.templateId,
            isActive: scheduleParams.isActive,
            updatedBy: userId,
          };

          if (updateRequest?.scheduleDetails?.recurring?.endCondition?.type === 'NEVER') {
            updateRequest.scheduleDetails.recurring.endCondition.endDate = undefined;
          }

          updateSchedule(updateRequest);
        } else {
          // Create new schedule
          const createRequest: CreateAuditScheduleRequest = {
            name: scheduleParams.name,
            description: scheduleParams.description,
            businessUnitId: scheduleParams.businessUnitId,
            program: scheduleParams.program,
            auditType: scheduleParams.auditType,
            scheduleType: scheduleParams.scheduleType,
            scheduleDetails: checkDatFormat(scheduleParams.scheduleDetails),
            auditExpirationSettings: {
              expirationEnabled: true,
              expirationDays: 30,
            },
            auditTemplateIds: scheduleParams.templateId,
            isActive: scheduleParams.isActive,
            createdBy: userId,
          };
          createSchedule(createRequest);
        }
      }
    };

    return (
      <SpaceBetween direction="horizontal" size="xs">
        <Button
          data-testid="cancel-button"
          formAction="none"
          variant="link"
          onClick={onCancelClick}
        >
          Cancel
        </Button>

        <Button
          data-testid="save-button"
          variant="primary"
          onClick={onSaveClick}
          loading={createScheduleApiStatus.isLoading || updateScheduleApiStatus.isLoading}
          disabled={
            Boolean(auditSchedule && !hasChanges()) || isCompleted || !isEditSchedule()
          }
        >
          Save
        </Button>
      </SpaceBetween>
    );
  };

  // Using the reusable ScheduleComponent instead of separate render functions

  return (
    <Container
      header={
        <SpaceBetween direction="vertical" size="s">
          {/* Show warning banner for completed schedules */}
          {auditSchedule && auditSchedule.status === ScheduleStatus.COMPLETED && (
            <Alert type="warning" header="Schedule Completed">
              This schedule has already executed and is completed. No edits are allowed.
            </Alert>
          )}
          <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
            <Header variant="h3">Audit Schedule</Header>
            <Box textAlign="right">
              {auditSchedule && !copyFromSchedule && isEditSchedule() && (
                <Button
                  variant="primary"
                  iconName="copy"
                  onClick={handleCopyToNew}
                  data-testid="copy-to-new-button"
                >
                  Copy to New Schedule
                </Button>
              )}
            </Box>
          </Grid>
        </SpaceBetween>
      }
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <ApiStatusFlashBar
          apiStatus={[createScheduleApiStatus, updateScheduleApiStatus]}
          clearMessages={() => {
            resetApiStatus();
          }}
        />

        <Form actions={renderActionButton()}>
          <ColumnLayout columns={1}>
            <SpaceBetween direction="vertical" size="l">
              {/* Show info banner for copy mode */}
              {copyFromSchedule && (
                <Alert type="info" header="Copy Schedule">
                  Schedule details copied from existing schedule.
                </Alert>
              )}

              <FormField
                data-testid="name-field"
                label={<RequiredFieldLabel label="Schedule Name" />}
                errorText={fieldValidationError[ScheduleValidationPaths.NAME.path]}
              >
                <Input
                  data-testid="name-input"
                  value={scheduleParams.name}
                  disabled={isCompleted || !isEditSchedule()}
                  onChange={({ detail }) =>
                    setScheduleParams((prevState) => ({
                      ...prevState,
                      name: detail.value,
                    }))
                  }
                />
              </FormField>

              <FormField
                data-testid="description-field"
                label="Schedule Description"
                errorText={fieldValidationError[ScheduleValidationPaths.DESCRIPTION.path]}
              >
                <Input
                  data-testid="description-input"
                  value={scheduleParams.description}
                  disabled={isCompleted || !isEditSchedule()}
                  onChange={({ detail }) =>
                    setScheduleParams((prevState) => ({
                      ...prevState,
                      description: detail.value,
                    }))
                  }
                />
              </FormField>

              <FormField
                data-testid="template-id-field"
                label={<RequiredFieldLabel label="Template" />}
                errorText={fieldValidationError[ScheduleValidationPaths.TEMPLATE_ID.path]}
              >
                <Multiselect
                  readOnly={templateId ? true : false}
                  disabled={isCompleted || !isEditSchedule()}
                  data-testid="templateId-multiselect"
                  selectedOptions={convertList<AuditTemplate, SelectProps.Option>(
                    templatesList.filter((data) =>
                      scheduleParams.templateId.includes(data.id),
                    ),
                    (item) => ({
                      value: item.id,
                      label: item.templateName,
                    }),
                  )}
                  onChange={({ detail }) =>
                    setScheduleParams((prevState) => ({
                      ...prevState,
                      templateId: convertList<SelectProps.Option, string>(
                        detail.selectedOptions as SelectProps.Option[],
                        (item) => item.value || '',
                      ),
                    }))
                  }
                  filteringType="auto"
                  options={convertList<AuditTemplate, SelectProps.Option>(
                    templatesList,
                    (item) => ({
                      value: item.id,
                      label: item.templateName,
                    }),
                  )} // Add your template options here
                  placeholder="Select templates"
                />
              </FormField>

              <FormField
                data-testid="audit-type-field"
                label={<RequiredFieldLabel label="Audit Type" />}
                errorText={fieldValidationError[ScheduleValidationPaths.AUDIT_TYPE.path]}
              >
                <Select
                  data-testid="audit-type-select"
                  disabled={isCompleted || !isEditSchedule()}
                  selectedOption={{
                    label:
                      AUDIT_TYPE_LABELS[
                        scheduleParams.auditType as keyof typeof AUDIT_TYPE_LABELS
                      ],
                    value: scheduleParams.auditType,
                  }}
                  onChange={({ detail }) =>
                    setScheduleParams((prevState) => ({
                      ...prevState,
                      auditType: detail.selectedOption.value || '',
                    }))
                  }
                  options={getAuditTypeOptions({
                    isEditCalibrationAllow: isAllowToCaliberationAudit(),
                    isEditProgressAllow: isAllowToProgressAudit(),
                    isEditSituationAllow: isAllowToSituationAudit(),
                    isEditTestAudit: isAllowToTestAudit(),
                    isEditMpaAllow: isAllowToMpaAudit(),
                  })}
                />
              </FormField>

              <FormField
                data-testid="schedule-type-field"
                label={<RequiredFieldLabel label="Schedule Type" />}
                errorText={
                  fieldValidationError[ScheduleValidationPaths.SCHEDULE_TYPE.path]
                }
              >
                <RadioGroup
                  items={[
                    { value: 'ONCE', label: 'Once' },
                    { value: 'RECURRING', label: 'Recurring' },
                  ]}
                  value={scheduleParams.scheduleType}
                  readOnly={isCompleted || !isEditSchedule()}
                  onChange={({ detail }) =>
                    setScheduleParams((prevState) => ({
                      ...prevState,
                      scheduleType: detail.value,
                    }))
                  }
                />
              </FormField>

              <ScheduleComponent
                scheduleType={scheduleParams.scheduleType as ScheduleType}
                scheduleDetails={scheduleParams.scheduleDetails}
                fieldValidationError={fieldValidationError}
                disabled={isCompleted || !isEditSchedule()}
                onChange={(details) =>
                  setScheduleParams((prev) => ({
                    ...prev,
                    scheduleDetails: details,
                  }))
                }
              />

              <FormField
                label={<RequiredFieldLabel label="Activation Status" />}
                constraintText="Enable or disable the schedule using this switch."
              >
                <Toggle
                  disabled={isCompleted || !isEditSchedule()}
                  onChange={({ detail }) => {
                    const newStatus = detail.checked;

                    if (!newStatus && scheduleParams.isActive) {
                      // Deactivating - show warning modal
                      setPendingStatusChange(newStatus);
                      setShowDeactivateModal(true);
                    } else if (newStatus && !scheduleParams.isActive) {
                      // Activating - show info modal
                      setPendingStatusChange(newStatus);
                      setShowActivateModal(true);
                    } else {
                      // Direct change for other cases
                      setScheduleParams((prev) => ({ ...prev, isActive: newStatus }));
                    }
                  }}
                  checked={scheduleParams.isActive}
                >
                  {scheduleParams.isActive ? 'Active' : 'Inactive'}
                </Toggle>
              </FormField>
            </SpaceBetween>
          </ColumnLayout>
        </Form>

        <WarningModal
          visible={showDeactivateModal || showActivateModal}
          title={showDeactivateModal ? '⚠️ Warning:' : 'Information:'}
          message={
            showDeactivateModal
              ? 'All future audits scheduled under this schedule will be cancelled. No new audits will be created from a disabled schedule. Existing audits (Not Started & In Progress) will not be affected.  Are you sure you wish to continue?'
              : 'New audits will be created based on this schedule on the next execution day.'
          }
          onOk={() => {
            if (pendingStatusChange !== null) {
              setScheduleParams((prev) => ({ ...prev, isActive: pendingStatusChange }));
            }
            setShowDeactivateModal(false);
            setShowActivateModal(false);
            setPendingStatusChange(null);
          }}
          onCancel={() => {
            setShowDeactivateModal(false);
            setShowActivateModal(false);
            setPendingStatusChange(null);
          }}
        />
      </form>
    </Container>
  );
};

export default AuditScheduleForm;
